import React, { useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { useGame } from '@/app/providers/GameContextProvider';
import { useGlobalPopup } from '@/app/providers/GlobalPopupProvider';
import { useGameModeSettingsContext } from '@/app/providers/GameModeSettingsProvider';
import { GameHazard } from '@/lib/types/common/GameMode';
import { HAZARD_DEFINITIONS } from '@/lib/config/hazardConfig';
import { HazardModeSettings, CustomModeSettings } from '@/lib/types/common/GameModeAdvancedSettings';
import ToggleFFXIV from '@/app/components/ToggleFFXIV';
import { IoChevronDown, IoChevronUp } from 'react-icons/io5';
import HazardIcon from '../../../HazardIcon';
import { hazards } from '../../../GamePlay/hazards';

const EnabledHazardsSelector: React.FC = () => {
    const { t } = useTranslation();
    const { room, gameState } = useGame();
    const { showPopup } = useGlobalPopup();
    const { settings, updateSettings, isGameMaster } = useGameModeSettingsContext();
    const [isExpanded, setIsExpanded] = useState(false);

    if (!room || !gameState) return null;

    const allHazards = Object.values(GameHazard);
    const value = (settings as HazardModeSettings | CustomModeSettings).enabledHazards ?? allHazards;

    // Check if all hazards are enabled
    const allEnabled = allHazards.every(hazard => value.includes(hazard));

    const handleToggleHazard = (hazard: string) => {
        if (!isGameMaster) {
            showPopup({
                type: 'error',
                message: t('lobby.onlyGameMasterCan'),
            });
            return;
        }

        if (value.includes(hazard)) {
            // Don't allow disabling all hazards - keep at least one
            if (value.length === 1) {
                showPopup({
                    type: 'error',
                    message: t('game.config.advancedSettings.atLeastOneHazard'),
                });
                return;
            }
            updateSettings({ enabledHazards: value.filter(h => h !== hazard) } as Partial<HazardModeSettings>);
        } else {
            updateSettings({ enabledHazards: [...value, hazard] } as Partial<HazardModeSettings>);
        }
    };

    const handleToggleAll = () => {
        if (!isGameMaster) {
            showPopup({
                type: 'error',
                message: t('lobby.onlyGameMasterCan'),
            });
            return;
        }

        if (allEnabled) {
            // If all are enabled, disable all except the first one
            updateSettings({ enabledHazards: [allHazards[0]] } as Partial<HazardModeSettings>);
        } else {
            // Enable all
            updateSettings({ enabledHazards: [...allHazards] } as Partial<HazardModeSettings>);
        }
    };

    const getHazardName = (hazard: string): string => {
        // Use translation key based on hazard name from HAZARD_DEFINITIONS
        const hazardDef = HAZARD_DEFINITIONS[hazard as GameHazard];
        if (hazardDef) {
            return t(`hazards.${hazardDef.name}`) || hazardDef.name;
        }
        return hazard;
    };

    return (
        <div className="flex flex-col gap-2">
            {/* Expandable Header */}
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className={`
                    flex items-center justify-between w-full py-0.5 rounded hover:bg-[#3A3A3A] transition-colors
                    ${!isGameMaster ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
                `}
                disabled={!isGameMaster}
            >
                <div className="flex items-center gap-2">
                    <div className="text-[#EEE1C5] text-shadow-border shadow-black text-lg">
                        {t('game.config.advancedSettings.enabledHazards')}
                    </div>
                    <span className="text-xs text-[#EEE1C5]/70">
                        ({value.length}/{allHazards.length})
                    </span>
                </div>
                {isExpanded ? (
                    <IoChevronUp className="text-[#EEE1C5]" />
                ) : (
                    <IoChevronDown className="text-[#EEE1C5]" />
                )}
            </button>

            {/* Expandable Content */}
            {isExpanded && (
                <div className="flex flex-col gap-2 pl-2 pr-2 py-2 bg-[#1A1A1A] rounded-md border border-[#3A3A3A]">
                    {/* Toggle All Button */}
                    <div className="flex items-center justify-between mb-2">
                        <button
                            onClick={handleToggleAll}
                            className={`
                                px-2 py-1 text-xs rounded transition-colors
                                ${allEnabled
                                    ? 'bg-[#FCD76D] text-black hover:bg-[#FCE08D]'
                                    : 'bg-[#3A3A3A] text-white hover:bg-[#4A4A4A]'
                                }
                                ${!isGameMaster ? 'cursor-not-allowed' : 'cursor-pointer'}
                            `}
                            disabled={!isGameMaster}
                        >
                            {allEnabled
                                ? t('game.config.advancedSettings.deselectAll')
                                : t('game.config.advancedSettings.selectAll')
                            }
                        </button>
                    </div>

                    {/* Hazard List */}
                    <div className="grid grid-cols-2 gap-2">
                        {allHazards.map((hazard) => {
                            const hazardObj = hazards.find(h => h.name === hazard.toLowerCase());
                            return (
                                <div key={hazard} className="flex items-center gap-2">
                                    {hazardObj && <HazardIcon hazard={hazardObj} size={20} tooltip={false} className={!value.includes(hazard) ? 'grayscale opacity-50' : ''} />}
                                    <ToggleFFXIV
                                        value={value.includes(hazard)}
                                        onChange={() => handleToggleHazard(hazard)}
                                        text={getHazardName(hazard)}
                                        shape='square'
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default EnabledHazardsSelector;
